<template>
  <b-col sm="13">
    <div class="card">
      <div class="card-header">
        Additional shipper information
      </div>
      <div class="card-body">
        <b-row>
          <b-col sm="6">
            <b-form-group
                label-cols-sm="4"
                :label="$t('createShipment.label.On behalf of Initials')"
                label-align-sm="left"
            >
              <b-input-group size="sm">
                <b-col sm="10">
                  <b-form-input
                      size="sm"
                      type="text"
                      v-model="shipment.shipFromBookingInitialsOnBehalfOf"
                      name="shipment.shipFromBookingInitialsOnBehalfOf"
                      aria-describedby="shipFromBookingInitialsOnBehalfOf-feedback"
                      maxlength="20"
                      :class="{
                      'is-invalid': hasBackendValidationError(
                        'shipFromBookingInitialsOnBehalfOf'
                      )
                    }"
                  />
                  <validationField
                      fieldName="createShipment.label.shipFromBookingInitialsOnBehalfOf"
                      :validator="v.shipment.shipFromBookingInitialsOnBehalfOf"
                      name="shipment.shipFromBookingInitialsOnBehalfOf.error"
                  />
                </b-col>
                
                <b-input-group-append v-if="required(fieldNames.SHIP_FROM_BOOKING_INITIALS)">
                  <div>
                    <requiredIcon :active="hasShipmentOptions" />
                  </div>
                </b-input-group-append>
              </b-input-group>
            </b-form-group>
          </b-col>
          <b-col sm="6">
            <b-form-group
                label-cols-sm="4"
                :label="$t('createShipment.label.On behalf of Department')"
                label-align-sm="left"
            >
              <div class="d-flex align-items-center">

                <!-- SELECT -->
                <div class="flex-grow-1">
                  <v-select
                      :dynamicWidth="true"
                      :select-on-tab="true"
                      :options="departments.filter(x => x.active)"
                      placeholder="Choose Department"
                      aria-describedby="shipFromBookingDepOnBehalfOf-feedback"
                      v-model="shipment.shipFromBookingDepOnBehalfOf"
                      label="label"
                      :reduce="d => d.id"
                      class="department-select"
                  />
                </div>

                <div class="ml-2">
                  <div class="btn-group department-btn-group">
                    <b-button size="sm" variant="primary" @click="openAddDepartment">Add</b-button>
                    <b-button size="sm" variant="secondary" @click="openManageDepartments">Manage</b-button>
                  </div>
                </div>


              </div>

              <validationField
                  fieldName="createShipment.label.shipFromBookingDepOnBehalfOf"
                  :validator="v.shipment.shipFromBookingDepOnBehalfOf"
                  name="shipment.shipFromBookingDepOnBehalfOf.error"
              />
            </b-form-group>
          </b-col>
        </b-row>
        <b-row>
          <b-col sm="6">
            <b-form-group
                label-cols-sm="4"
                :label="$t('createShipment.label.Initials')"
                label-align-sm="left"
            >
              {{ userInitials }}
            </b-form-group>
          </b-col>
          <b-col sm="6">
            <b-form-group
                label-cols-sm="4"
                :label="$t('createShipment.label.Department')"
                label-align-sm="left"
            >
              {{ userDepartment }}
            </b-form-group>
          </b-col>
        </b-row>
      </div>
    </div>

    <!-- Add Department Modal -->
    <b-modal id="add-department" title="New Department" @ok="createDepartment">
      <b-form-input v-model="newDepartmentName" placeholder="Department name" />
    </b-modal>

    <!-- Manage Departments Modal -->
    <b-modal id="manage-departments" title="Manage Departments">
      <b-table :items="departments" :fields="['label','active','toggle']">
        <template #cell(toggle)="row">
          <b-form-checkbox
              v-model="row.item.active"
              @change="toggleDepartment(row.item)"
          />
        </template>
      </b-table>
    </b-modal>
  </b-col>
</template>

<script>
import viewOptions from '../mixins/viewOptionsMixin';
import { mapGetters } from 'vuex';
import { FIELD_NAMES } from "@/store/nameConstants";
import { yoyoHttp } from '../services/http.service';
import vSelect from './vSelect';
import requiredIcon from "@/components/requiredIcon.vue";
import validationMaxLength from "@/components/Validation/ValidationMaxLength.vue";
import validationRequired from "@/components/Validation/ValidationRequired.vue";
import validationField from "@/components/Validation/ValidationField.vue";

export default {
  components: {
    validationField,
    validationRequired,
    validationMaxLength,
    requiredIcon,
    vSelect
  },
  mixins: [viewOptions],
  props: {
    shipment: { required: true },
    v: { required: true }
  },
  computed: {
    ...mapGetters('createShipment', ['getSelectedCompanyAccount','hasShipmentOptions','hasBackendValidationError']),
    ...mapGetters('core', ['getUserInitials', 'getUserDepartment']),
    userInitials() {
      this.shipment.shipFromBookingInitials = this.getUserInitials;
      return this.getUserInitials;
    },
    userDepartment() {
      this.shipment.shipFromBookingDepartment = this.getUserDepartment;
      return this.getUserDepartment;
    }
  },
  async mounted() {
    await this.reloadDepartments();
  },
  data() {
    return {
      fieldNames: FIELD_NAMES,
      departments: [],
      newDepartmentName: ""
    };
  },
  watch: {
    'getSelectedCompanyAccount.id': {
      immediate: true,
      async handler(newVal, oldVal) {
        if (!newVal) {
          this.departments = [];
          this.shipment.shipFromBookingDepOnBehalfOf = null;
          return;
        }

        if (newVal !== oldVal) {
          this.shipment.shipFromBookingDepOnBehalfOf = null;
          this.shipment.shipFromBookingInitialsOnBehalfOf = null;
        }

        await this.reloadDepartments();
      }
    }
  },
  methods: {
    async reloadDepartments() {
      const r = await yoyoHttp.get('api/Shipment/GetBookingOptionsDepartmentsNewest', {
        params: {
          accountId: this.getSelectedCompanyAccount.id
        }
      });

      this.departments = r.data;
    },

    openAddDepartment() {
      this.newDepartmentName = '';
      this.$bvModal.show('add-department');
    },

    async createDepartment() {
      await yoyoHttp.post('api/Shipment/AddDepartment', {
        accountId: this.getSelectedCompanyAccount.id,
        name: this.newDepartmentName
      });
      await this.reloadDepartments();
    },

    openManageDepartments() {
      this.$bvModal.show('manage-departments');
    },

    async toggleDepartment(dep) {
      await yoyoHttp.post('api/Shipment/SetDepartmentActive', {
        id: dep.id,
        active: !dep.active
      });
      await this.reloadDepartments();
    }
  }
};
</script>

<style scoped>
.font-weight-bold {
  font-weight: bold;
}
.department-select .vs__dropdown-toggle {
  height: 32px !important;
  min-height: 32px !important;
  padding: 0 6px !important;
}

.department-select .vs__selected-options {
  padding: 0 !important;
  margin: 0 !important;
  display: flex;
  align-items: center;
  height: 32px !important;
}

.department-select .vs__actions {
  height: 32px !important;
  display: flex;
  align-items: center;
}

.department-select .vs__search {
  margin: 0 !important;
  padding: 0 !important;
  height: 30px !important;
}

.department-select .vs__search::placeholder {
  line-height: 32px !important;
}


</style>
